import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { projects, filterCategories } from "@/data/projects";

const EASE = [0.25, 0.1, 0.25, 1];

function CornerNodes() {
  const pos = [
    "left-0 top-0 -translate-x-1/2 -translate-y-1/2",
    "right-0 top-0 translate-x-1/2 -translate-y-1/2",
    "left-0 bottom-0 -translate-x-1/2 translate-y-1/2",
    "right-0 bottom-0 translate-x-1/2 translate-y-1/2",
  ];
  return (
    <>
      {pos.map((p, i) => (
        <span
          key={i}
          className={`pointer-events-none absolute z-20 h-2 w-2 rounded-full border border-[#1B3818] bg-white opacity-0 transition-all duration-500 group-hover:scale-125 group-hover:opacity-100 ${p}`}
          style={{ transitionTimingFunction: "var(--ama-ease)" }}
        />
      ))}
    </>
  );
}

function ProjectCard({ project, index, aspect, colSpan }) {
  const navigate = useNavigate();
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.7, ease: EASE }}
      className={colSpan}
      data-testid={`project-card-${project.id}`}
    >
      <div
        data-cursor="open"
        onClick={() => navigate(`/projects/${project.id}`)}
        className="group relative"
      >
        <div className={`relative w-full overflow-hidden ${aspect}`}>
          <CornerNodes />
          {/* thin green outline on hover */}
          <span className="pointer-events-none absolute inset-0 z-10 border border-transparent transition-colors duration-500 group-hover:border-[#1B3818]" />
          {/* mask wipe reveal */}
          <motion.span
            className="absolute inset-0 z-10 bg-white"
            initial={{ scaleY: 1 }}
            whileInView={{ scaleY: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE, delay: 0.1 }}
            style={{ transformOrigin: "top" }}
          />
          <img
            src={project.cover}
            alt={project.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
            style={{ transitionTimingFunction: "var(--ama-ease)" }}
          />
        </div>

        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <h3
              className="text-lg font-normal tracking-tight transition-colors duration-500 group-hover:text-[#1B3818] md:text-xl"
              style={{ transitionTimingFunction: "var(--ama-ease)" }}
            >
              {project.title}
            </h3>
            <p className="mt-1 ama-label text-black/45">
              {project.category} — {project.location}
            </p>
          </div>
          <span className="mono-num ama-label text-black/30">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

function getLayout(i, total) {
  if (i === total - 1 && total % 2 === 1 && total > 1) {
    return { aspect: "aspect-[16/9]", colSpan: "md:col-span-12" };
  }
  if (total === 1) return { aspect: "aspect-[16/9]", colSpan: "md:col-span-12" };
  const pattern = [
    { aspect: "aspect-[4/3]", colSpan: "md:col-span-7" },
    { aspect: "aspect-[4/5]", colSpan: "md:col-span-5 md:mt-24" },
    { aspect: "aspect-[4/5]", colSpan: "md:col-span-5" },
    { aspect: "aspect-[4/3]", colSpan: "md:col-span-7 md:mt-24" },
  ];
  return pattern[i % pattern.length];
}

export default function ProjectsGrid() {
  const [active, setActive] = useState("All");
  const filtered =
    active === "All"
      ? projects
      : projects.filter((p) => p.discipline === active);

  return (
    <section
      id="projects"
      className="relative z-10 mx-auto max-w-[1600px] scroll-mt-24 px-6 py-28 md:px-12 md:py-40"
      data-testid="projects-section"
    >
      <div className="mb-12 flex items-end justify-between border-b border-[#e4e4e2] pb-6 md:mb-16">
        <div>
          <span className="ama-label text-[#1B3818]">Selected Works</span>
          <h2 className="mt-4 text-4xl font-light tracking-tight md:text-6xl">
            Projects
          </h2>
        </div>
        <span className="mono-num ama-label text-black/40">
          {String(filtered.length).padStart(2, "0")} /{" "}
          {String(projects.length).padStart(2, "0")}
        </span>
      </div>

      {/* discipline filter */}
      <div
        className="mb-14 flex flex-wrap items-center gap-x-8 gap-y-3 md:mb-20"
        data-testid="project-filters"
      >
        {filterCategories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase()}`}
            data-cursor="link"
            onClick={() => setActive(c)}
            className="relative ama-label"
            style={{
              color: active === c ? "#1B3818" : "rgba(0,0,0,0.4)",
              transition: "color 400ms var(--ama-ease)",
            }}
          >
            {c}
            <span
              className="absolute -bottom-1.5 left-0 h-px bg-[#1B3818]"
              style={{
                width: active === c ? "100%" : "0%",
                transition: "width 450ms var(--ama-ease)",
              }}
            />
          </button>
        ))}
      </div>

      <motion.div
        layout
        className="grid grid-cols-1 gap-x-6 gap-y-16 md:grid-cols-12 md:gap-y-6"
      >
        <AnimatePresence mode="popLayout">
          {filtered.map((p, i) => {
            const l = getLayout(i, filtered.length);
            return (
              <ProjectCard
                key={p.id}
                project={p}
                index={i}
                aspect={l.aspect}
                colSpan={l.colSpan}
              />
            );
          })}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
